import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Grid, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext.js';
import { apiClient } from '../../services/api.js';

const AIConfiguration = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.roles?.includes('Administrator');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ apiKey: '', prompt: '' });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiClient.get('/ai/config');
        const data = res.data || {};
        setForm({ apiKey: data.apiKey || '', prompt: data.prompt || '' });
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load AI configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const payload = { apiKey: form.apiKey, prompt: form.prompt };
      const res = await apiClient.put('/ai/config', payload);
      setSuccess('Configuration saved');
      setForm({ apiKey: res.data?.apiKey || form.apiKey, prompt: res.data?.prompt || form.prompt });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save AI configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography variant="body1">You do not have permission to access this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>AI Configuration</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key"
                name="apiKey"
                value={form.apiKey}
                onChange={handleChange}
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prompt"
                name="prompt"
                value={form.prompt}
                onChange={handleChange}
                multiline
                rows={6}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default AIConfiguration;

